/**
 * Утилита для сжатия изображений перед загрузкой
 * Уменьшает размер файлов для экономии трафика и ускорения загрузки
 */

import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  preserveExif?: boolean;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxSizeMB: 1, // Максимальный размер 1MB
  maxWidthOrHeight: 1920, // Макс. разрешение 1920px
  useWebWorker: true, // Использовать Web Worker для производительности
  preserveExif: false, // Не сохранять EXIF данные (GPS координаты и т.д.)
};

const THUMBNAIL_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.1, // 100KB для превью
  maxWidthOrHeight: 300,
  useWebWorker: true,
  preserveExif: false,
};

/**
 * Сжатие основного изображения
 */
export async function compressImage(
  file: File,
  options?: CompressionOptions
): Promise<File> {
  try {
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const compressedFile = await imageCompression(file, mergedOptions);
    
    // Сохраняем оригинальное имя файла
    return new File([compressedFile], file.name, {
      type: compressedFile.type,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error('Ошибка сжатия изображения:', error);
    // В случае ошибки возвращаем оригинальный файл
    return file;
  }
}

/**
 * Создание миниатюры (thumbnail) для быстрой загрузки и превью
 */
export async function createThumbnail(file: File): Promise<Blob> {
  try {
    return await imageCompression(file, THUMBNAIL_OPTIONS);
  } catch (error) {
    console.error('Ошибка создания миниатюры:', error);
    // В случае ошибки создаём минимальное превью вручную
    return createFallbackThumbnail(file);
  }
}

/**
 * Создание превью через Canvas API (fallback)
 */
async function createFallbackThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        
        // Вычисляем размеры с сохранением пропорций
        const maxSize = 300;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/jpeg',
          0.7
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Проверка, является ли файл изображением
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Валидация размера файла
 */
export function validateFileSize(file: File, maxSizeMB: number = 10): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}

/**
 * Получение информации об изображении
 */
export async function getImageInfo(file: File): Promise<{
  width: number;
  height: number;
  aspectRatio: number;
  sizeKB: number;
}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          aspectRatio: img.width / img.height,
          sizeKB: Math.round(file.size / 1024),
        });
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Форматирование размера файла для отображения
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Пакетное сжатие нескольких изображений
 */
export async function compressMultipleImages(
  files: File[],
  options?: CompressionOptions,
  onProgress?: (current: number, total: number) => void
): Promise<File[]> {
  const compressed: File[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    if (isImageFile(file)) {
      const compressedFile = await compressImage(file, options);
      compressed.push(compressedFile);
    } else {
      compressed.push(file);
    }
    
    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }
  
  return compressed;
}
